import type { Surface, WarpPresetV2 } from '@cornerstone/shared';
import { API_BASE } from './config.js';

// =============================================================================
// Calibration v2 REST client — PLAN §6 / P2 서버 계약.
// 기존 warp preset 라우터와 같은 표면이 `/api/warp-presets-v2`에 병행 마운트
// 된다는 계약으로 작성 (payload = WarpPresetV2). live warp는 기존
// POST /api/control/warp/live 와 대칭인 `/api/control/warp/live-v2`에
// LiveWarpV2 payload를 보낸다.
// =============================================================================

const BASE = API_BASE;

/** 실시간 동기화 payload — P2 계약. */
export type LiveWarpV2 = {
  surfaces: Surface[];
  bypassWarp: boolean;
};

export type NewPresetV2Input = Omit<WarpPresetV2, 'id' | 'createdAt' | 'lastSavedAt'>;

/** v2 export 파일 — schemaVersion 2. 구버전(1) 파일은 import에서 거부. */
export type WarpPresetV2ExportFile = {
  schemaVersion: 2;
  exportedAt: string;
  presets: WarpPresetV2[];
};

/**
 * 서버 부트스트랩 상태의 v2 조각 — GET /api/state 응답에서 optional 필드로
 * 읽는다 (P2가 병행 구현 중이라 없을 수 있음 → null 처리).
 */
export type ServerLiveStateV2 = {
  activePresetId: string | null;
  live: LiveWarpV2;
};

async function json<T>(r: Response): Promise<T> {
  if (!r.ok) {
    const text = await r.text().catch(() => '');
    throw new Error(`${r.status} ${r.statusText}${text ? ': ' + text : ''}`);
  }
  return r.json() as Promise<T>;
}

async function ok(r: Response): Promise<void> {
  if (!r.ok) {
    const text = await r.text().catch(() => '');
    throw new Error(`${r.status} ${r.statusText}${text ? ': ' + text : ''}`);
  }
}

export const apiV2 = {
  // -------- Presets (기기별 격리 — 목록·생성·수출입은 deviceId 필수) --------
  listPresets: (deviceId: string): Promise<WarpPresetV2[]> =>
    fetch(
      `${BASE}/api/warp-presets-v2?deviceId=${encodeURIComponent(deviceId)}`,
    ).then(json<WarpPresetV2[]>),

  getPreset: (id: string): Promise<WarpPresetV2> =>
    fetch(`${BASE}/api/warp-presets-v2/${id}`).then(json<WarpPresetV2>),

  createPreset: (input: NewPresetV2Input, rememberForDevice = false): Promise<WarpPresetV2> =>
    fetch(`${BASE}/api/warp-presets-v2`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...input, rememberForDevice }),
    }).then(json<WarpPresetV2>),

  updatePreset: (id: string, patch: Partial<WarpPresetV2>, rememberForDevice = false): Promise<WarpPresetV2> =>
    fetch(`${BASE}/api/warp-presets-v2/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...patch, rememberForDevice }),
    }).then(json<WarpPresetV2>),

  deletePreset: (id: string): Promise<void> =>
    fetch(`${BASE}/api/warp-presets-v2/${id}`, { method: 'DELETE' }).then(ok),

  duplicatePreset: (id: string): Promise<WarpPresetV2> =>
    fetch(`${BASE}/api/warp-presets-v2/${id}/duplicate`, {
      method: 'POST',
    }).then(json<WarpPresetV2>),

  setPresetProtected: (id: string, value: boolean): Promise<WarpPresetV2> =>
    fetch(`${BASE}/api/warp-presets-v2/${id}/protect`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ protected: value }),
    }).then(json<WarpPresetV2>),

  exportPresets: (deviceId: string): Promise<WarpPresetV2ExportFile> =>
    fetch(
      `${BASE}/api/warp-presets-v2/export?deviceId=${encodeURIComponent(deviceId)}`,
    ).then(json<WarpPresetV2ExportFile>),

  importPresets: (
    deviceId: string,
    file: WarpPresetV2ExportFile,
  ): Promise<{ imported: WarpPresetV2[] }> =>
    fetch(
      `${BASE}/api/warp-presets-v2/import?deviceId=${encodeURIComponent(deviceId)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(file),
      },
    ).then(json<{ imported: WarpPresetV2[] }>),

  // -------- Live sync (기기별 — deviceId 필수) --------
  liveWarp: (deviceId: string, body: LiveWarpV2, signal?: AbortSignal): Promise<void> =>
    fetch(`${BASE}/api/control/warp/live-v2`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ deviceId, ...body }),
      ...(signal ? { signal } : {}),
    }).then(ok),

  /**
   * 부트스트랩 — GET /api/state에서 v2 조각을 읽는다. 서버가 아직 v2 상태를
   * 노출하지 않으면(필드 없음) null을 돌려주고, 호출부는 기본값으로 시작한다.
   */
  getLiveState: async (deviceId: string): Promise<ServerLiveStateV2 | null> => {
    const state = await fetch(
      `${BASE}/api/state?deviceId=${encodeURIComponent(deviceId)}`,
    ).then(
      json<{
        currentPresetId?: string | null;
        currentPresetIdV2?: string | null;
        liveWarpV2?: { surfaces?: Surface[]; bypassWarp?: boolean };
      }>,
    );
    const live = state.liveWarpV2;
    if (!live || !Array.isArray(live.surfaces)) return null;
    return {
      activePresetId: state.currentPresetIdV2 ?? null,
      live: { surfaces: live.surfaces, bypassWarp: live.bypassWarp === true },
    };
  },

  /** 저장된 preset을 Player의 live 상태에 적용하고 재시작 복원 대상으로 기록. */
  applyPreset: (deviceId: string, presetId: string): Promise<WarpPresetV2> =>
    fetch(`${BASE}/api/control/warp/apply-preset-v2`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ preset_id: presetId, deviceId }),
    }).then(json<WarpPresetV2>),
};
